import { usePreferences } from '../../lib/PreferencesContext'
import { moveWidget, resolveLayout, sizesFor, updateWidget } from '../../lib/preferences'
import { CHART_NAMES, SIZE_NAMES } from './WidgetCard'
import { WIDGETS, WIDGET_BY_ID } from './widgets'

/**
 * Which widgets appear, in what order, drawn how.
 *
 * Move buttons rather than drag-and-drop, and that is still the rule this list follows. Dragging is
 * unreachable from a keyboard without a second implementation and awkward on the touch screens half
 * this console is read on; two buttons per row do the same job, work everywhere, and can be described
 * in the label a screen reader announces.
 *
 * The board itself now offers a drag as well, from a grip on each card. That is an addition beside
 * the buttons rather than a reversal of this: the buttons are on the cards too, they remain the only
 * way that works for everybody, and this list remains the only way to bring back a widget that is
 * switched off - a hidden widget has no card to put a control on.
 *
 * Every change saves immediately. There is no Save button because there is nothing to lose by saving
 * — the operator is looking at the result as they make it, and a form that asks them to confirm an
 * arrangement they can already see is a form asking a question it has answered itself.
 */
export function WidgetChooser() {
  const { dashboardLayout, setDashboardLayout } = usePreferences()
  const layout = resolveLayout(dashboardLayout, WIDGETS)

  return (
    <ul className="widget-chooser" data-testid="widget-chooser">
      {layout.map((choice, index) => {
        const widget = WIDGET_BY_ID.get(choice.widget)
        if (!widget) return null

        return (
          <li key={choice.widget} className="widget-row">
            <label className="widget-toggle">
              <input
                type="checkbox"
                checked={choice.visible}
                onChange={(event) =>
                  setDashboardLayout(updateWidget(layout, choice.widget, { visible: event.target.checked }))
                }
                data-testid={`widget-visible-${choice.widget}`}
              />
              <span>
                <span className="widget-name">{widget.title}</span>
                <span className="hint">{widget.description}</span>
              </span>
            </label>

            <div className="widget-controls">
              {sizesFor(widget).length > 1 && (
                <select
                  aria-label={`How wide ${widget.title} is`}
                  value={choice.size ?? widget.defaultSize}
                  onChange={(event) =>
                    setDashboardLayout(updateWidget(layout, choice.widget, { size: event.target.value }))
                  }
                  data-testid={`widget-size-${choice.widget}`}
                >
                  {sizesFor(widget).map((size) => (
                    <option key={size} value={size}>
                      {SIZE_NAMES[size]}
                    </option>
                  ))}
                </select>
              )}

              {widget.charts.length > 1 && (
                <select
                  aria-label={`How to draw ${widget.title}`}
                  value={choice.chart}
                  onChange={(event) =>
                    setDashboardLayout(updateWidget(layout, choice.widget, { chart: event.target.value }))
                  }
                  data-testid={`widget-chart-${choice.widget}`}
                >
                  {widget.charts.map((kind) => (
                    <option key={kind} value={kind}>
                      {CHART_NAMES[kind]}
                    </option>
                  ))}
                </select>
              )}

              <button
                type="button"
                aria-label={`Move ${widget.title} up`}
                disabled={index === 0}
                onClick={() => setDashboardLayout(moveWidget(layout, choice.widget, -1))}
              >
                <span aria-hidden="true">↑</span>
              </button>
              <button
                type="button"
                aria-label={`Move ${widget.title} down`}
                disabled={index === layout.length - 1}
                onClick={() => setDashboardLayout(moveWidget(layout, choice.widget, 1))}
              >
                <span aria-hidden="true">↓</span>
              </button>
            </div>
          </li>
        )
      })}
    </ul>
  )
}
